import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../../lib/constants';
import { DRAFT_KEYS, loadDraft, saveDraft, clearDraft } from '../../../../utils/draftUtils';
import { useNotification } from '../../../../contexts/NotificationContext';

export const useConsiderationPhase = (uhid_no, navigate) => {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [patient, setPatient] = useState({ uhid_no: '', name: '' });
  const [dataLoaded, setDataLoaded] = useState(false);

  const [formData, setFormData] = useState({
    uhid_no: uhid_no || '',
    date_of_birth: '',
    emergency_contact: '',
    existing_medical_conditions: '',
    past_surgeries: '',
    current_medications: '',
    fasting_acknowledgment: false,
    medication_adjustment_acknowledgment: false,
    benefits_of_surgery: '',
    risks_of_surgery: '',
    alternatives_to_surgery: '',
    pre_surgical_tests: {
      cbc: false,
      blood_sugar: false,
      liver_tests: false,
      kidney_tests: false,
      xray: false,
      ecg: false,
      ultrasound: false,
      urine_test: false,
      covid_test: false,
    },
    planned_surgical_date: '',
    surgery_type: '',
    planned_anesthesia_type: '',
    consent_surgery: false,
    consent_blood_transfusion: false,
    consent_anesthesia: false,
  });

  const fetchPatientData = async (uhidNo) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/get_patient_consideration/${uhidNo}`);
      const data = response.data;
      const formattedDOB = data.date_of_birth
        ? new Date(data.date_of_birth).toISOString().slice(0, 10)
        : '';

      setPatient(data);
      setFormData(prev => ({
        ...prev,
        uhid_no: data.uhid_no,
        date_of_birth: formattedDOB,
      }));
    } catch (error) {
      console.error('Error fetching patient data:', error);
      showNotification('error', 'Failed to fetch patient data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchConsiderationData = useCallback(async (uhidNo) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/get_consideration_phase/${uhidNo}`);
      if (response.data) {
        const data = response.data;
        const formattedDOB = data.date_of_birth
          ? new Date(data.date_of_birth).toISOString().slice(0, 10)
          : '';

        const formattedSurgicalDate = data.planned_surgical_date
          ? new Date(data.planned_surgical_date).toISOString().slice(0, 10)
          : '';

        const defaultPreSurgicalTests = {
          cbc: false,
          blood_sugar: false,
          liver_tests: false,
          kidney_tests: false,
          xray: false,
          ecg: false,
          ultrasound: false,
          urine_test: false,
          covid_test: false,
        };

        let preSurgicalTests = defaultPreSurgicalTests;
        if (Array.isArray(data.pre_surgical_tests)) {
          const base = { ...defaultPreSurgicalTests };
          Object.keys(base).forEach(k => { base[k] = false; });
          data.pre_surgical_tests.forEach(k => {
            if (k in base) base[k] = true;
          });
          preSurgicalTests = base;
        } else if (typeof data.pre_surgical_tests === 'object' && data.pre_surgical_tests !== null) {
          preSurgicalTests = { ...defaultPreSurgicalTests, ...data.pre_surgical_tests };
        }

        setFormData(prev => ({
          ...prev,
          ...data,
          date_of_birth: formattedDOB,
          planned_surgical_date: formattedSurgicalDate,
          pre_surgical_tests: preSurgicalTests,
          fasting_acknowledgment: !!data.fasting_acknowledgment,
          medication_adjustment_acknowledgment: !!data.medication_adjustment_acknowledgment,
          consent_surgery: !!data.consent_surgery,
          consent_blood_transfusion: !!data.consent_blood_transfusion,
          consent_anesthesia: !!data.consent_anesthesia,
        }));

        setSuccess(true);
      }
    } catch (err) {
      console.log("No existing consideration form found.");
    } finally {
      setDataLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (uhid_no) {
      fetchPatientData(uhid_no);
      fetchConsiderationData(uhid_no);
    }
  }, [uhid_no, fetchConsiderationData]);

  // Draft handles
  useEffect(() => {
    if (!dataLoaded) return;
    const draft = loadDraft(DRAFT_KEYS.CONSIDERATION_PHASE, uhid_no);
    if (draft && draft.formData) {
      setFormData(prev => ({ ...prev, ...draft.formData }));
    }
  }, [uhid_no, dataLoaded]);

  useEffect(() => {
    if (!dataLoaded || success) return;
    const timeoutId = setTimeout(() => {
      saveDraft(DRAFT_KEYS.CONSIDERATION_PHASE, uhid_no, formData);
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [formData, dataLoaded, success, uhid_no]);

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case 'date_of_birth':
        if (!value) {
          error = 'Date of Birth is required';
        } else {
          const selectedDate = new Date(value);
          const today = new Date();
          selectedDate.setHours(0, 0, 0, 0);
          today.setHours(0, 0, 0, 0);
          if (selectedDate >= today) error = 'DOB must be before today';
        }
        break;
      case 'emergency_contact':
        if (!value || (typeof value === 'string' && value.trim() === '')) error = 'Emergency Contact is required';
        break;
      case 'existing_medical_conditions':
        if (!value || !value.trim()) error = 'Existing Medical Conditions is required';
        break;
      case 'surgery_type':
        if (!value || !value.trim()) error = 'Surgery Type is required';
        break;
      case 'benefits_of_surgery':
        if (!value || !value.trim()) error = 'Benefits of Surgery is required';
        break;
      case 'risks_of_surgery':
        if (!value || !value.trim()) error = 'Risks of Surgery is required';
        break;
      case 'alternatives_to_surgery':
        if (!value || !value.trim()) error = 'Alternatives to Surgery is required';
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return error === "";
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value,
        },
      }));
    } else if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    let tempErrors = {};
    if (!formData.date_of_birth) tempErrors.date_of_birth = 'Date of Birth is required';
    if (!formData.emergency_contact) tempErrors.emergency_contact = 'Emergency Contact is required';
    if (!formData.existing_medical_conditions?.trim()) tempErrors.existing_medical_conditions = 'Medical Conditions is required';
    if (!formData.surgery_type?.trim()) tempErrors.surgery_type = 'Surgery Type is required';
    if (!formData.fasting_acknowledgment) tempErrors.fasting_acknowledgment = 'Acknowledgement required';
    if (!formData.medication_adjustment_acknowledgment) tempErrors.medication_adjustment_acknowledgment = 'Acknowledgement required';
    if (!formData.consent_surgery) tempErrors.consent_surgery = 'Consent required';
    if (!formData.consent_blood_transfusion) tempErrors.consent_blood_transfusion = 'Consent required';
    if (!formData.consent_anesthesia) tempErrors.consent_anesthesia = 'Consent required';
    
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!validate()) {
      showNotification('error', "Please fill all required fields.");
      return;
    }

    const selectedTests = Object.keys(formData.pre_surgical_tests)
      .filter(test => formData.pre_surgical_tests[test]);

    const submitData = {
      ...formData,
      hospital_id: 1,
      pre_surgical_tests: selectedTests,
      fasting_acknowledgment: formData.fasting_acknowledgment ? 1 : 0,
      medication_adjustment_acknowledgment: formData.medication_adjustment_acknowledgment ? 1 : 0,
      consent_surgery: formData.consent_surgery ? 1 : 0,
      consent_blood_transfusion: formData.consent_blood_transfusion ? 1 : 0,
      consent_anesthesia: formData.consent_anesthesia ? 1 : 0,
    };

    try {
      setLoading(true);
      if (success) {
        await axios.put(`${API_BASE_URL}/update_consideration_phase/${uhid_no}`, submitData, { withCredentials: true });
        showNotification('success', "Updated successfully!");
      } else {
        await axios.post(`${API_BASE_URL}/consideration_phase`, submitData, { withCredentials: true });
        showNotification('success', 'Submitted successfully!');
      }
      clearDraft(DRAFT_KEYS.CONSIDERATION_PHASE, uhid_no);
      navigate(`/mainPage/${uhid_no}`);
    } catch (error) {
      console.error('Submission error:', error);
      showNotification('error', `Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return {
    formData, setFormData, errors, loading, success, patient,
    handleChange, handleSubmit, validateField
  };
};
